import type { ProjectRow } from '../transport/api'
import type { ConversationRow } from '../chatv1/transport'

/**
 * WHICH ROOM a message came out of — owner, 2026-08-22: 要把 project chat 的 title
 * 放進去.
 *
 * A `<chat-msg:CONV …>` / `<project-msg:ID …>` frame carries the room's ID and no
 * name, which is right on the wire: the id is what never changes, and the lists this
 * app already polls turn it into a title. So this resolves against those lists rather
 * than asking the edge to widen the frame — no release, and the title the transcript
 * shows is by construction the same one the rail shows.
 *
 * UNRESOLVED IS A REAL STATE, not a bug to paper over. Clean View is read by the
 * OWNER, and an agent can be attached to a conversation the owner is not in — their
 * project list will never carry it. Callers render nothing then. The alternative,
 * printing the raw id, spends a whole line on sixteen hex digits nobody can read;
 * the id is kept in the tooltip, where it is the reply address it always was.
 */
export type RoomTitle = { id: string; title: string }

/**
 * The rooms this viewer can name, from the two lists they already hold.
 *
 * DMs are included even though a DM is not a "project chat": leaving them out would
 * mean one kind of conversation silently loses its label while another keeps it, and
 * a reader cannot tell that apart from "the title failed to load". A DM's name is the
 * person on the other end — the same thing the rail calls it.
 */
export function roomTitles(
  projects: ProjectRow[] | undefined,
  dms: ConversationRow[] | undefined,
): RoomTitle[] {
  const out: RoomTitle[] = []
  for (const p of projects ?? []) {
    const title = typeof p.name === 'string' ? p.name.trim() : ''
    if (p.project_id && title) out.push({ id: p.project_id, title })
  }
  for (const c of dms ?? []) {
    const title = (c.peer?.name ?? c.peer?.email ?? '').trim()
    if (c.conv_id && title) out.push({ id: c.conv_id, title })
  }
  return out
}

/** The title for one room id, or null when this viewer cannot name it. */
export function titleFor(rooms: RoomTitle[] | undefined, id: string | undefined | null): string | null {
  if (!id) return null
  return rooms?.find((r) => r.id === id)?.title ?? null
}

/**
 * A fingerprint of what `titleFor` would answer.
 *
 * Every rendered message is memoised, and the resolver is a stable callback reading a
 * ref — so nothing repaints when the lists refetch and come back identical. This is
 * what tells the memo that an answer actually CHANGED (a room renamed, or the list
 * arriving for the first time after the transcript did). Sorted, because ordering
 * churn in a list response is not a change.
 */
export function roomsKey(rooms: RoomTitle[] | undefined): string {
  return (rooms ?? [])
    .map((r) => `${r.id}:${r.title}`)
    .sort()
    .join('|')
}
